import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getUserTransactions } from '../services/walletServiceEnhanced';
import Loader from '../components/Loader';

import {
    isCredit,
    isReversed,
    txTypeLabel as getTypeLabel,
    txIcon as getTypeIcon,
    txSubtitle as getTypeSubtitle,
    timeAgo,
    formatUsd,
    txRowAmountColor,
    txAmountPrefix,
} from '../utils/transactionDisplay';

function groupByDate(transactions) {
    const groups = {};
    transactions.forEach(tx => {
        const date = new Date(tx.created_at);
        const now = new Date();
        const diffDays = Math.floor((now - date) / 86400000);
        let label;
        if (diffDays === 0) label = 'Today';
        else if (diffDays === 1) label = 'Yesterday';
        else label = date.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
        if (!groups[label]) groups[label] = [];
        groups[label].push(tx);
    });
    return groups;
}

function Notifications() {
    const { user } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (user?.id) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const loadData = async () => {
        const result = await getUserTransactions(user.id);
        if (!result.error && result.data) setTransactions(result.data);
        setLoading(false);
    };

    if (loading) {
        return (
            <main className="main-sectionWrapper">
                <div id="main-section" className="primary-scroll">
                    <div style={s.headerBar}>
                        <Link to="/welcome" style={s.backBtn} aria-label="Back">
                            <i className='bx bx-chevron-left' style={s.backIcon}></i>
                        </Link>
                        <span style={s.headerTitle}>Notifications</span>
                        <div style={{ width: 38 }} />
                    </div>
                    <Loader />
                </div>
            </main>
        );
    }

    const grouped = groupByDate(transactions);
    const groupKeys = Object.keys(grouped);

    return (
        <main className="main-sectionWrapper">
            <div id="main-section" className="primary-scroll">

                <div style={s.headerBar}>
                    <Link to="/welcome" style={s.backBtn} aria-label="Back">
                        <i className='bx bx-chevron-left' style={s.backIcon}></i>
                    </Link>
                    <span style={s.headerTitle}>Notifications</span>
                    <div style={{ width: 38 }} />
                </div>

                <div style={s.container}>

                    {transactions.length === 0 ? (
                        <div style={s.emptyState}>
                            <div style={s.emptyIconWrap}>
                                <i className="bx bx-bell" style={{ fontSize: 32, color: 'rgba(255,255,255,0.3)' }} />
                            </div>
                            <p style={s.emptyText}>No notifications yet</p>
                            <p style={s.emptySubtext}>Your wallet activity will appear here</p>
                        </div>
                    ) : (
                        groupKeys.map(groupLabel => (
                            <div key={groupLabel} style={s.group}>
                                <div style={s.groupLabel}>{groupLabel}</div>
                                <div style={s.groupCard}>
                                    {grouped[groupLabel].map((tx, idx) => {
                                        const credit = isCredit(tx.transaction_type);
                                        const iconColor = txRowAmountColor(tx, credit);
                                        const iconBg = credit ? 'rgba(5,150,105,0.15)' : 'rgba(59,130,246,0.12)';
                                        const isLast = idx === grouped[groupLabel].length - 1;
                                        return (
                                            <div
                                                key={tx.id}
                                                style={{ ...s.notifRow, ...(!isLast ? s.notifBorder : {}) }}
                                            >
                                                <div style={{ ...s.notifIconWrap, background: iconBg }}>
                                                    <i
                                                        className={`bx ${getTypeIcon(tx.transaction_type)}`}
                                                        style={{ color: iconColor, fontSize: 18 }}
                                                    />
                                                </div>
                                                <div style={s.notifInfo}>
                                                    <div style={s.notifTitle}>{getTypeLabel(tx.transaction_type)}</div>
                                                    <div style={s.notifSubtitle}>{getTypeSubtitle(tx)}</div>
                                                    <div style={s.notifTime}>{timeAgo(tx.created_at)}</div>
                                                </div>
                                                <div style={{
                                                    ...s.notifAmount,
                                                    color: txRowAmountColor(tx, credit),
                                                    textDecoration: isReversed(tx) ? 'line-through' : 'none',
                                                }}>
                                                    {txAmountPrefix(tx, credit)}${formatUsd(tx.amount_usd)}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))
                    )}

                    {transactions.length > 0 && (
                        <p style={s.footerNote}>
                            {transactions.length} notification{transactions.length !== 1 ? 's' : ''} total
                        </p>
                    )}
                </div>
            </div>
        </main>
    );
}

const s = {
    headerBar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 14px',
        background: 'rgba(14,7,88,0.88)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
    },
    backBtn: {
        width: 38,
        height: 38,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textDecoration: 'none',
        flexShrink: 0,
    },
    backIcon: { color: '#fff', fontSize: 22 },
    headerTitle: {
        color: '#fff',
        fontSize: 16,
        fontWeight: 700,
        letterSpacing: '-0.2px',
        flex: 1,
        textAlign: 'center',
    },
    container: {
        padding: '16px 16px 96px',
        maxWidth: 600,
        margin: '0 auto',
    },
    group: { marginBottom: 20 },
    groupLabel: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: 11,
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: 1,
        marginBottom: 8,
        paddingLeft: 4,
    },
    groupCard: {
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 20,
        overflow: 'hidden',
    },
    notifRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '14px 16px',
    },
    notifBorder: { borderBottom: '1px solid rgba(255,255,255,0.04)' },
    notifIconWrap: {
        width: 44,
        height: 44,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    notifInfo: { flex: 1, minWidth: 0 },
    notifTitle: {
        color: '#fff',
        fontSize: 14,
        fontWeight: 500,
        marginBottom: 2,
    },
    notifSubtitle: {
        color: 'rgba(255,255,255,0.45)',
        fontSize: 12,
        marginBottom: 2,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    notifTime: { color: 'rgba(255,255,255,0.3)', fontSize: 11 },
    notifAmount: {
        fontSize: 15,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },
    emptyState: {
        textAlign: 'center',
        padding: '60px 20px',
    },
    emptyIconWrap: {
        width: 64,
        height: 64,
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
    },
    emptyText: {
        color: 'rgba(255,255,255,0.55)',
        fontSize: 15,
        fontWeight: 600,
        margin: '0 0 6px',
    },
    emptySubtext: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 13,
        margin: 0,
    },
    footerNote: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: 12,
        textAlign: 'center',
        margin: 0,
    },
};

export default Notifications;
